<div class="py-2 sm:py-6 px-4 sm:px-6 lg:px-8">
  <!-- Header -->
  <div id="order-summary-header" class="text-center mb-6 sm:mb-8">
    <h2 class="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{{ 'Podsumowanie zamówienia' | translate }}</h2>
    <p class="text-sm sm:text-base text-gray-500 mt-1">{{ 'Sprawdź dane zamówienia przed jego potwierdzeniem.' | translate }}</p>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
    <!-- Order Items Section -->
    <div class="lg:col-span-2 space-y-6">
      <!-- Items Card -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 class="text-base font-bold text-gray-900">{{ 'Produkty w zamówieniu' | translate }}</h3>
            <p class="text-xs text-gray-500">
              {{ 'Liczba produktów:' | translate }} {{ cart.length }}
              <span aria-hidden="true">·</span>
              {{ 'Łączna liczba sztuk:' | translate }} {{ getTotalQuantity() }}
            </p>
          </div>
        </div>

        <div class="divide-y divide-gray-100">
          <div *ngFor="let item of cart; let i = index"
            class="group flex items-start gap-3 sm:gap-4 p-4 sm:p-5 hover:bg-gray-50/50 transition-colors">
            <!-- Product Image -->
            <div class="relative shrink-0">
              <div
                class="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden group-hover:shadow-md transition-shadow">
                <img [src]="item?.product?.documents[0]?.url || 'https://via.placeholder.com/80'" alt=""
                  class="w-full h-full object-cover" />
              </div>
              <!-- Quantity Badge -->
              <div
                class="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-brocom-orange text-white text-xs font-bold rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                {{ item?.quantity }}
              </div>
            </div>

            <!-- Product Details -->
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 mb-1 text-sm sm:text-base truncate">{{ item?.product?.label }}</h4>
              <div class="space-y-1 text-xs sm:text-sm text-gray-600">
                <p class="flex items-center gap-2">
                  <span class="w-2 h-2 bg-gray-300 rounded-full"></span>
                  {{ 'Ilość:' | translate }} {{ item?.quantity }}
                </p>

                <!-- Variant values -->
                <p *ngIf="hasVariantValues(item)" class="flex items-start gap-2">
                  <span class="w-2 h-2 bg-gray-300 rounded-full mt-1"></span>
                  <span class="text-gray-700">{{ 'Warianty:' | translate }}
                    <span class="font-medium text-gray-900">{{ getVariantValues(item).join(', ') }}</span>
                  </span>
                </p>

                <!-- Selected color swatch and name -->
                <p *ngIf="item?.selectedColor?.colorName" class="flex items-center gap-2">
                  <span class="w-3 h-3 rounded-full border border-gray-300"
                    [style.background-color]="getColorHex(item.selectedColor.colorName)"></span>
                  <span class="text-gray-700">{{ 'Kolor:' | translate }} <span class="font-medium text-gray-900">{{
                      item.selectedColor.colorName }}</span></span>
                </p>

                <!-- Selected Certificates / Downloads -->
                <div *ngIf="item?.selectedDownloads && item?.selectedDownloads.length > 0" class="pt-1">
                  <div class="flex items-center gap-2 text-gray-700 font-medium">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-3 h-3 text-gray-500">
                      <path stroke-linecap="round" stroke-linejoin="round"
                        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                    <span>{{ 'Usługi dodatkowe' | translate }}</span>
                  </div>
                  <div class="mt-1 min-w-0 space-y-1">
                    <div *ngFor="let download of item?.selectedDownloads"
                      class="grid min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-start gap-x-4">
                      <div class="flex min-w-0 flex-1 items-start gap-2 text-gray-600">
                        <span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300"></span>
                        <span class="min-w-0 flex-1 whitespace-normal break-words leading-5">{{ download?.name | translate }}</span>
                      </div>
                      <span class="min-w-[5.5rem] whitespace-nowrap text-right text-gray-900">
                        {{ (download?.price || 0) | appendCurrency:shippingCurrency }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Price -->
            <div class="text-right shrink-0">
              <p class="text-base sm:text-lg font-bold text-gray-900">{{ formatMoney(getItemTotal(item)) }}</p>
              <p class="text-xs text-gray-500">{{ 'Wartość pozycji' | translate }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Shipping & Billing Information Card -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div>
            <h3 class="text-base font-bold text-gray-900">{{ 'Dane do faktury i dostawy' | translate }}</h3>
            <p class="text-xs text-gray-500">{{ 'Adres dostawy i dane kontaktowe' | translate }}</p>
          </div>
          <span
            class="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-800"
            [attr.aria-label]="'Rodzaj nabywcy: ' + buyerTypeLabel">
            {{ buyerTypeLabel }}
          </span>
        </div>

        <div class="p-5 sm:p-6">
          <div *ngIf="formGroup?.get('step2')?.value as billingInfo" class="grid grid-cols-1 gap-5"
            [ngClass]="(billingInfo.companyName || billingInfo.vatNumber || billingInfo.deliverySameAsBilling === false) ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'">
            <!-- 1. Company Details (Only if companyName or vatNumber provided) -->
            <div *ngIf="billingInfo.companyName || billingInfo.vatNumber"
              class="bg-gray-50/70 rounded-xl p-4 sm:p-5 border border-gray-100 flex flex-col justify-start">
              <div class="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-gray-200/60">
                <div class="w-2.5 h-2.5 rounded-full bg-brocom-orange"></div>
                <span class="text-xs font-bold text-gray-600 uppercase tracking-wider">{{ 'Dane firmy' | translate }}</span>
              </div>
              <div class="space-y-3">
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Firma' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-semibold text-gray-900 block mt-0.5">{{ billingInfo.companyName }}</span>
                </div>
                <div *ngIf="billingInfo.industrysector">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Branża / dział' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-700 block mt-0.5">{{ billingInfo.industrysector }}</span>
                </div>
                <div *ngIf="billingInfo.vatNumber">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'NIP' | translate }}</span>
                  <span translate="no"
                    class="notranslate text-xs font-mono font-medium text-gray-900 bg-white px-2 py-0.5 rounded border border-gray-200 inline-block mt-0.5">{{
                    billingInfo.vatNumber }}</span>
                </div>
              </div>
            </div>

            <!-- 2. Address Details -->
            <div class="bg-gray-50/70 rounded-xl p-4 sm:p-5 border border-gray-100 flex flex-col justify-start">
              <div class="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-gray-200/60">
                <div class="w-2.5 h-2.5 rounded-full bg-brocom-orange"></div>
                <span class="text-xs font-bold text-gray-600 uppercase tracking-wider">{{ 'Address Details' | translate }}</span>
              </div>
              <div class="space-y-3">
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Ulica i numer' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-semibold text-gray-900 block mt-0.5">{{ billingInfo.postcodeTown || '—'
                    }}</span>
                </div>
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Miasto i kod pocztowy' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-900 block mt-0.5">{{ billingInfo.city }}{{
                    billingInfo.postalCode ? (', ' + billingInfo.postalCode) : '' }}</span>
                </div>
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Kraj' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-900 block mt-0.5">
                    {{ getCountryName(billingInfo.otherCountry) }}
                    <span *ngIf="billingInfo.otherCountry" class="text-xs font-mono text-gray-400">({{
                      billingInfo.otherCountry }})</span>
                  </span>
                </div>
                <div *ngIf="billingInfo.stateProvince">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Województwo / region' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-700 block mt-0.5">{{ billingInfo.stateProvince }}</span>
                </div>
                <div *ngIf="billingInfo.otherAddressInfo">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Dodatkowe informacje' | translate }}</span>
                  <span translate="no" class="notranslate text-xs text-gray-600 block mt-0.5 italic">{{ billingInfo.otherAddressInfo }}</span>
                </div>
              </div>
            </div>

            <!-- Separate delivery address -->
            <div *ngIf="billingInfo.deliverySameAsBilling === false"
              class="bg-gray-50/70 rounded-xl p-4 sm:p-5 border border-gray-100 flex flex-col justify-start">
              <div class="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-gray-200/60">
                <div class="w-2.5 h-2.5 rounded-full bg-brocom-orange"></div>
                <span class="text-xs font-bold text-gray-600 uppercase tracking-wider">{{ 'Adres dostawy' | translate }}</span>
              </div>
              <div class="space-y-3">
                <div *ngIf="billingInfo.deliveryRecipientName">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Odbiorca' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-semibold text-gray-900 block mt-0.5">{{ billingInfo.deliveryRecipientName }}</span>
                </div>
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Ulica i numer' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-semibold text-gray-900 block mt-0.5">{{ billingInfo.deliveryAddress || '—' }}</span>
                </div>
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Miasto i kod pocztowy' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-900 block mt-0.5">
                    {{ billingInfo.deliveryCity || '—' }}{{ billingInfo.deliveryPostalCode ? (', ' + billingInfo.deliveryPostalCode) : '' }}
                  </span>
                </div>
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Kraj' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-900 block mt-0.5">
                    {{ getCountryName(billingInfo.deliveryCountry) || '—' }}
                    <span *ngIf="billingInfo.deliveryCountry" class="text-xs font-mono text-gray-400">({{ billingInfo.deliveryCountry }})</span>
                  </span>
                </div>
                <div *ngIf="billingInfo.deliveryStateProvince">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Województwo / region' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-700 block mt-0.5">{{ billingInfo.deliveryStateProvince }}</span>
                </div>
              </div>
            </div>

            <!-- 3. Contact Information -->
            <div class="bg-gray-50/70 rounded-xl p-4 sm:p-5 border border-gray-100 flex flex-col justify-start">
              <div class="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-gray-200/60">
                <div class="w-2.5 h-2.5 rounded-full bg-brocom-orange"></div>
                <span class="text-xs font-bold text-gray-600 uppercase tracking-wider">{{ 'Dane kontaktowe' | translate }}</span>
              </div>
              <div class="space-y-3">
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Imię i nazwisko' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-semibold text-gray-900 block mt-0.5">{{ billingInfo.firstName }} {{
                    billingInfo.lastName }}</span>
                </div>
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Adres e-mail' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-900 block mt-0.5 break-words">{{ billingInfo.email || '—'
                    }}</span>
                </div>
                <div>
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Numer telefonu' | translate }}</span>
                  <span translate="no" class="notranslate text-sm font-medium text-gray-900 block mt-0.5">{{ billingInfo.phoneNumber ||
                    billingInfo.otherPhone || '—' }}</span>
                </div>
              </div>
            </div>

            <!-- 4. Other Details (Purple dot - matches Step 3) -->
            <div *ngIf="billingInfo.orderingParty || billingInfo.departmentOrder || billingInfo.noteBrocom"
              class="bg-gray-50/70 rounded-xl p-4 sm:p-5 border border-gray-100 flex flex-col justify-start"
              [ngClass]="(billingInfo.companyName || billingInfo.vatNumber) ? 'col-span-1 md:col-span-2 lg:col-span-3' : 'col-span-1 md:col-span-2'">
              <div class="flex items-center gap-2 mb-3 sm:mb-4">
                <div class="w-2.5 h-2.5 rounded-full bg-brocom-orange"></div>
                <span class="text-xs font-bold text-gray-500 uppercase tracking-wider">{{ 'Informacje dodatkowe' | translate }}</span>
              </div>
              <div class="grid grid-cols-1 gap-3 sm:gap-4 text-xs sm:text-sm"
                [ngClass]="(billingInfo.orderingParty && billingInfo.departmentOrder) ? 'sm:grid-cols-2 md:grid-cols-3' : (billingInfo.orderingParty || billingInfo.departmentOrder ? 'sm:grid-cols-2' : 'grid-cols-1')">
                <div *ngIf="billingInfo.orderingParty">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Osoba składająca zamówienie' | translate }}</span>
                  <span translate="no" class="notranslate font-semibold text-gray-900 block mt-0.5">{{ billingInfo.orderingParty }}</span>
                </div>
                <div *ngIf="billingInfo.departmentOrder">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Dział / numer zamówienia' | translate }}</span>
                  <span translate="no" class="notranslate font-semibold text-gray-900 block mt-0.5">{{ billingInfo.departmentOrder }}</span>
                </div>
                <div *ngIf="billingInfo.noteBrocom" class="sm:col-span-2 md:col-span-1">
                  <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block">{{ 'Uwagi dla BROCOM' | translate }}</span>
                  <span translate="no" class="notranslate font-semibold text-gray-900 block mt-0.5">{{ billingInfo.noteBrocom }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Summary Sidebar -->
    <div class="lg:col-span-1">
      <div class="sticky top-8 flex flex-col gap-3.5 sm:gap-4">
        <!-- Order Total Card -->
        <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div class="px-5 py-4 border-b border-gray-100">
            <div>
              <h3 class="text-base font-bold text-gray-900">{{ 'Podsumowanie kosztów' | translate }}</h3>
              <p class="text-xs text-gray-500">{{ 'Szczegółowe zestawienie cen' | translate }}</p>
            </div>
          </div>

          <div class="p-4 sm:p-6 space-y-3 sm:space-y-4">
            <div class="space-y-2 sm:space-y-3">
              <div class="flex justify-between items-center py-1.5 sm:py-2">
                <span class="text-gray-600 text-sm sm:text-base">{{ 'Wartość produktów netto' | translate }}</span>
                <span class="font-semibold text-gray-900 text-sm sm:text-base">{{ formatMoney(getSubtotal()) }}</span>
              </div>

              <div class="flex justify-between items-center py-1.5 sm:py-2">
                <span class="text-gray-600 text-sm sm:text-base">{{ 'VAT' | translate }}</span>
                <span class="font-semibold text-gray-900 text-sm sm:text-base">{{ formatMoney(getTax()) }}</span>
              </div>

              <!-- Downloads Section (only show if there are downloads) -->
              <div *ngIf="hasDownloads()" class="flex justify-between items-center py-1.5 sm:py-2">
                <span class="text-gray-600 text-sm sm:text-base">
                  {{ 'Usługi dodatkowe' | translate }}
                </span>
                <span class="font-semibold text-gray-900 text-sm sm:text-base">{{ formatMoney(getDownloads()) }}</span>
              </div>

              <!-- Shipping Section - FedEx Live Rates -->
              <div class="py-2">
                <!-- Shipping Label Row -->
                <div class="flex items-center justify-between gap-3">
                  <span class="flex items-center gap-2 text-sm text-gray-600 sm:text-base">
                    {{ 'Dostawa' | translate }}
                    <!-- FedEx Badge -->
                    <span *ngIf="isFedEx && !isLoadingShipping && !shippingError"
                      class="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                      <svg aria-hidden="true" class="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 3h18v18H3z" fill="none" />
                        <path
                          d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l7.5 3.75-7.5 3.75L4.5 7.93 12 4.18zM4 9.5l7 3.5v7l-7-3.5V9.5zm9 10.5v-7l7-3.5v7L13 20z" />
                      </svg>
                      {{ 'Aktualna stawka FedEx' | translate }}
                    </span>
                  </span>

                  <!-- Loading spinner -->
                  <span *ngIf="isLoadingShipping" class="flex items-center gap-1.5 text-gray-400 text-xs">
                    <svg class="h-3.5 w-3.5 animate-spin text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none"
                      viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    {{ 'Pobieranie stawek…' | translate }}
                  </span>

                  <!-- Error indicator -->
                  <span *ngIf="!isLoadingShipping && shippingError" class="text-xs font-semibold text-red-600">
                    {{ 'Niedostępne' | translate }}
                  </span>

                  <!-- Price display when not loading and no error -->
                  <span *ngIf="!isLoadingShipping && !shippingError" class="text-sm font-semibold text-gray-900 sm:text-base">
                    {{ shippingAmount === 0 ? 'Bezpłatnie' : formatMoney(shippingAmount) }}
                  </span>
                </div>

                <!-- Shipping Error Alert -->
                <div *ngIf="shippingError && !isLoadingShipping"
                  class="mt-2 mb-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-start gap-2">
                  <svg class="w-4 h-4 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <span class="font-semibold block text-red-800">{{ 'Stawki za dostawę są niedostępne' | translate }}</span>
                    <span>{{ 'Nie udało się połączyć z przewoźnikiem. Skontaktuj się z obsługą klienta, aby dokończyć zamówienie.' | translate }}</span>
                    <a href="mailto:sklep@brocomindustry.com?subject=Shipping%20rate%20assistance"
                      class="mt-1 inline-flex font-semibold text-red-800 underline underline-offset-2">
                      {{ 'Skontaktuj się z obsługą klienta' | translate }}
                    </a>
                  </div>
                </div>

                <!-- Multiple shipping options selector (only when FedEx returns >1 option) -->
                <div *ngIf="!isLoadingShipping && shippingOptions.length > 1" class="mt-3 flex flex-col gap-2"
                  role="radiogroup" [attr.aria-label]="'Usługa dostawy' | translate">
                  <label *ngFor="let option of shippingOptions"
                    class="flex cursor-pointer items-center justify-between rounded-lg border p-2.5 text-xs transition-colors sm:px-3 sm:py-2.5 sm:text-sm"
                    [class.border-brocom-orange]="selectedShippingOption?.serviceType === option.serviceType"
                    [class.bg-orange-50]="selectedShippingOption?.serviceType === option.serviceType"
                    [class.ring-1]="selectedShippingOption?.serviceType === option.serviceType"
                    [class.ring-brocom-orange]="selectedShippingOption?.serviceType === option.serviceType"
                    [class.border-gray-200]="selectedShippingOption?.serviceType !== option.serviceType"
                    [class.bg-white]="selectedShippingOption?.serviceType !== option.serviceType">
                    <div class="flex items-start gap-2.5 min-w-0 pr-2">
                      <input type="radio" name="summaryShippingOption"
                        [checked]="selectedShippingOption?.serviceType === option.serviceType"
                        [attr.aria-label]="option.serviceName + ', ' + formatMoney(getOptionPrice(option))"
                        (change)="selectShippingOption(option)"
                        class="mt-0.5 h-4 w-4 flex-shrink-0 accent-orange-600 focus:ring-orange-500" />
                      <div class="min-w-0 flex-1">
                        <div class="font-medium text-gray-800 leading-snug whitespace-nowrap overflow-hidden text-ellipsis"
                          [title]="option.serviceName">{{ option.serviceName }}</div>
                        <div class="flex items-center gap-1.5 mt-1">
                          <span class="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                            <svg class="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {{ getDeliveryBadge(option).text }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span class="font-bold text-gray-900 flex-shrink-0 text-right whitespace-nowrap">
                      {{ option.totalPrice === 0 ? 'Bezpłatnie' : formatMoney(getOptionPrice(option)) }}
                    </span>
                  </label>
                </div>

                <!-- Single option delivery days hint -->
                <div *ngIf="!isLoadingShipping && shippingOptions.length === 1 && selectedShippingOption"
                  class="mt-2 flex flex-col items-start gap-1 pl-0.5">
                  <span class="text-xs font-medium text-gray-700">{{ selectedShippingOption.serviceName }}</span>
                  <span class="inline-flex items-start gap-1.5 text-xs leading-5 text-gray-500">
                    <svg aria-hidden="true" class="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {{ getDeliveryBadge(selectedShippingOption).text }}
                  </span>
                </div>

              </div>


              <!-- Voucher Discount (if applied) -->
              <div *ngIf="voucherDiscountAmount > 0"
                class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 py-1.5 text-emerald-700 sm:py-2">
                <span class="flex min-w-0 flex-col items-start gap-1 text-sm font-medium sm:text-base">
                  <span class="flex min-w-0 items-center gap-2">
                    <span class="h-2 w-2 shrink-0 rounded-full bg-emerald-500"></span>
                    <span>{{ 'Voucher Discount' | translate }}</span>
                  </span>
                  <span *ngIf="appliedVoucher?.code"
                    class="ml-4 max-w-full rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-mono leading-4 break-all">
                    {{ appliedVoucher.code }}
                  </span>
                </span>
                <span class="whitespace-nowrap text-right text-sm font-semibold sm:text-base">
                  -{{ formatMoney(getVoucherDiscount()) }}
                </span>
              </div>

              <hr class="border-gray-200">

              <div
                class="flex justify-between items-center py-2 sm:py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl px-3 sm:px-4">
                <span class="text-lg sm:text-xl font-bold text-gray-900">{{ 'Razem' | translate }}</span>
                <span *ngIf="!hasShippingError && !isLoadingShipping"
                  class="text-xl sm:text-2xl font-bold text-brocom-orange">{{ formatMoney(totalAmount) }}</span>
                <span *ngIf="hasShippingError || isLoadingShipping"
                  class="text-sm font-semibold text-gray-500">{{ 'Dostępne po potwierdzeniu kosztu dostawy' | translate }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Legal terms and optional marketing preferences -->
        <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
          <h4 class="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
            <div class="w-2 h-2 bg-brocom-orange rounded-full"></div>
            {{ 'Przed złożeniem zamówienia' | translate }}
          </h4>

          <div class="space-y-3 sm:space-y-4" [formGroup]="step4FormGroup" *ngIf="step4FormGroup">
            <label class="flex items-start gap-2 sm:gap-3 cursor-pointer group">
              <input id="acceptTerms" type="checkbox" formControlName="acceptTerms"
                class="mt-0.5 sm:mt-1 w-4 h-4 text-brocom-orange border-gray-300 rounded focus:ring-brocom-orange focus:ring-2"
                required aria-required="true"
                [attr.aria-describedby]="step4FormGroup.get('acceptTerms')?.invalid && step4FormGroup.get('acceptTerms')?.touched ? 'terms-validation-error' : 'terms-required-hint'"
                [attr.aria-invalid]="step4FormGroup.get('acceptTerms')?.invalid && step4FormGroup.get('acceptTerms')?.touched"
                [class.border-red-500]="step4FormGroup.get('acceptTerms')?.invalid && step4FormGroup.get('acceptTerms')?.touched">
              <div class="flex-1">
                <span class="text-xs sm:text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                  {{ 'Zapoznałem(-am) się z dokumentami i akceptuję' | translate }}
                  <a routerLink="/legal/terms-and-conditions" target="_blank" rel="noopener"
                    class="text-brocom-orange hover:text-orange-600 font-medium underline decoration-dotted">{{ 'Regulamin' | translate }}</a>
                  {{ 'oraz' | translate }}
                  <a routerLink="/legal/privacy-policy" target="_blank" rel="noopener"
                    class="text-brocom-orange hover:text-orange-600 font-medium underline decoration-dotted">{{ 'Politykę prywatności' | translate }}</a>
                  <span class="text-orange-500">*</span>
                </span>
                <div *ngIf="step4FormGroup.get('acceptTerms')?.invalid && step4FormGroup.get('acceptTerms')?.touched"
                  id="terms-validation-error"
                  class="text-red-500 text-xs mt-1">
                  {{ 'Aby kontynuować, zaakceptuj Regulamin i Politykę prywatności.' | translate }}
                </div>
                <div *ngIf="!step4FormGroup.get('acceptTerms')?.touched"
                  id="terms-required-hint" class="text-gray-500 text-xs mt-1">
                  {{ 'Akceptacja jest wymagana, aby przejść do płatności.' | translate }}
                </div>
              </div>
            </label>

            <label for="subscribeNewsletter" class="flex items-start gap-2 sm:gap-3 cursor-pointer group">
              <input id="subscribeNewsletter" type="checkbox" formControlName="subscribeNewsletter"
                class="mt-0.5 sm:mt-1 w-4 h-4 text-brocom-orange border-gray-300 rounded focus:ring-brocom-orange focus:ring-2">
              <span class="flex-1 text-xs sm:text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                {{ 'Chcę otrzymywać okazjonalne wiadomości o nowościach i ofertach. Zgoda jest dobrowolna i nie wpływa na realizację zamówienia.' | translate }}
              </span>
            </label>
          </div>
        </div>

        <div *ngIf="shippingStatusMessage" class="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          role="status" aria-live="polite">
          <p>{{ shippingStatusMessage }}</p>
          <button *ngIf="!isLoadingShipping && isShippingAddressComplete"
            type="button" (click)="fetchShippingRates(true)"
            class="mt-2 font-semibold text-orange-700 underline underline-offset-2">
            {{ 'Ponów pobieranie stawek za dostawę' | translate }}
          </button>
        </div>

        <div *ngIf="paymentError" class="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          role="alert" aria-live="assertive">
          {{ paymentError }}
        </div>

        <!-- Confirm Order Button -->
        <button type="button" (click)="onConfirmOrder()"
          [disabled]="isConfirmDisabled"
          [class.cursor-not-allowed]="isConfirmDisabled && !isProcessingOrder"
          [class.cursor-wait]="isProcessingOrder"
          [class.bg-gray-100]="isConfirmDisabled && !isProcessingOrder"
          [class.text-gray-400]="isConfirmDisabled && !isProcessingOrder"
          [class.border]="isConfirmDisabled && !isProcessingOrder"
          [class.border-gray-200]="isConfirmDisabled && !isProcessingOrder"
          [class.bg-orange-600]="isProcessingOrder"
          [class.bg-gradient-to-r]="!isConfirmDisabled"
          [class.from-brocom-orange]="!isConfirmDisabled"
          [class.to-orange-600]="!isConfirmDisabled"
          [class.text-white]="!isConfirmDisabled || isProcessingOrder"
          [class.hover:from-orange-600]="!isConfirmDisabled"
          [class.hover:to-orange-700]="!isConfirmDisabled"
          [attr.aria-busy]="isProcessingOrder"
          class="w-full min-h-14 py-3.5 sm:py-4 px-4 sm:px-6 rounded-xl font-semibold text-base transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-orange-200 focus:ring-offset-2 disabled:opacity-100">
          <span class="flex items-center justify-center gap-2 sm:gap-3">
            <!-- Loading Spinner -->
            <svg *ngIf="isProcessingOrder" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            <!-- Lock Icon when terms are not accepted or shipping rate unavailable -->
            <svg *ngIf="!isProcessingOrder && isConfirmDisabled"
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
              class="w-5 h-5 text-gray-400">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            <!-- Success Icon (when not processing, form is valid, and shipping is available) -->
            <svg *ngIf="!isProcessingOrder && step4FormGroup.valid && !isLoadingShipping && !hasShippingError"
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5"
              stroke="currentColor" class="w-5 h-5 sm:w-6 sm:h-6">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
            </svg>
            <span class="text-sm sm:text-base">
              <span *ngIf="isProcessingOrder">{{ 'Przygotowywanie bezpiecznej płatności…' | translate }}</span>
              <span *ngIf="!isProcessingOrder && isLoadingShipping">{{ 'Sprawdzanie kosztu dostawy…' | translate }}</span>
              <span *ngIf="!isProcessingOrder && !isLoadingShipping && hasShippingError">{{ 'Koszt dostawy jest niedostępny' | translate }}</span>
              <span *ngIf="!isProcessingOrder && step4FormGroup.invalid">{{ 'Zaakceptuj wymagane warunki, aby kontynuować.' | translate }}</span>
              <span *ngIf="!isProcessingOrder && step4FormGroup.valid && !isLoadingShipping && !hasShippingError">
                {{ paymentError ? 'Ponów płatność' : 'Przejdź do płatności' }}
              </span>
            </span>
          </span>
        </button>

        <div *ngIf="isProcessingOrder"
          class="mt-3 flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-gray-700"
          role="status" aria-live="polite">
          <svg class="mt-0.5 h-4 w-4 shrink-0 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p>{{ 'Przygotowujemy sesję płatności. Nie zamykaj tej strony — gdy będzie gotowa, bezpiecznie przekierujemy Cię dalej.' | translate }}</p>
        </div>

        <!-- Security Badge -->
        <div class="bg-gray-50/90 rounded-xl sm:rounded-2xl p-3.5 sm:p-4 border border-gray-200 shadow-sm">
          <div class="flex items-center gap-3">
            <div class="bg-emerald-100 text-emerald-600 rounded-full p-2 shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                stroke="currentColor" class="w-4 h-4 sm:w-5 sm:h-5">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
            <div>
              <p class="text-xs sm:text-sm font-semibold text-gray-800">{{ 'Bezpieczne składanie zamówienia' | translate }}</p>
              <p class="text-xs text-gray-500">{{ 'Twoje dane są chronione 256-bitowym szyfrowaniem SSL.' | translate }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>